import React, { useCallback, useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { AuthContext } from '../App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { toast } from 'sonner';
import { ChevronDown, ChevronUp, HandHelping, Trash2, Edit3, Check, Sparkles, Users } from 'lucide-react';
import GroupPrayerComments from './GroupPrayerComments';

/**
 * MyGroupPrayers
 * Shows an expandable feed of prayers for each group the user is a member of.
 * Members can post prayers, mark them answered, edit/delete their own, and
 * "I prayed for this" on any group prayer. All actions hit the same endpoints
 * used by the main Prayer Wall.
 */
const MyGroupPrayers = () => {
  const { user, API } = useContext(AuthContext);
  const [groups, setGroups] = useState([]);
  const [loadingGroups, setLoadingGroups] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [prayersByGroup, setPrayersByGroup] = useState({});
  const [loadingGroupPrayers, setLoadingGroupPrayers] = useState(null);
  const [prayingId, setPrayingId] = useState(null);

  // Edit dialog
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingPrayer, setEditingPrayer] = useState(null);
  const [editText, setEditText] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);

  const fetchGroups = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/groups/my`);
      setGroups(res.data || []);
    } catch (e) {
      // Silent — MyGroupsPanel above will surface any error.
    } finally {
      setLoadingGroups(false);
    }
  }, [API]);

  useEffect(() => {
    fetchGroups();
  }, [fetchGroups]);

  const fetchGroupPrayers = useCallback(async (groupId) => {
    setLoadingGroupPrayers(groupId);
    try {
      const res = await axios.get(`${API}/prayers/group/${groupId}`);
      setPrayersByGroup((prev) => ({ ...prev, [groupId]: res.data || [] }));
    } catch (e) {
      toast.error('Failed to load prayers for this group');
    } finally {
      setLoadingGroupPrayers(null);
    }
  }, [API]);

  const toggleGroup = (groupId) => {
    const next = expandedId === groupId ? null : groupId;
    setExpandedId(next);
    if (next) fetchGroupPrayers(next);
  };

  const refreshCurrent = () => {
    if (expandedId) fetchGroupPrayers(expandedId);
  };

  const handlePray = async (prayer) => {
    if (prayingId === prayer.id) return;
    setPrayingId(prayer.id);
    try {
      const res = await axios.post(`${API}/prayers/${prayer.id}/pray`);
      const { prayer_count, has_prayed } = res.data || {};
      setPrayersByGroup((prev) => {
        const list = prev[prayer.group_id] || [];
        return {
          ...prev,
          [prayer.group_id]: list.map((p) =>
            p.id === prayer.id
              ? {
                  ...p,
                  prayer_count,
                  prayer_user_ids: has_prayed
                    ? Array.from(new Set([...(p.prayer_user_ids || []), user.id]))
                    : p.prayer_user_ids,
                }
              : p,
          ),
        };
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not record prayer');
    } finally {
      setPrayingId(null);
    }
  };

  const handleMarkAnswered = async (prayer, answered) => {
    try {
      await axios.put(`${API}/prayers/${prayer.id}`, { answered });
      toast.success(answered ? 'Marked as answered! Praise God!' : 'Unmarked as answered');
      refreshCurrent();
    } catch {
      toast.error('Failed to update prayer');
    }
  };

  const handleDelete = async (prayer) => {
    if (!window.confirm('Delete this prayer?')) return;
    try {
      await axios.delete(`${API}/prayers/${prayer.id}`);
      toast.success('Prayer deleted');
      refreshCurrent();
    } catch {
      toast.error('Failed to delete prayer');
    }
  };

  const openEdit = (prayer) => {
    setEditingPrayer(prayer);
    setEditText(prayer.prayer_text || '');
    setEditDialogOpen(true);
  };

  const submitEdit = async () => {
    if (!editingPrayer) return;
    const trimmed = editText.trim();
    if (!trimmed) {
      toast.error('Prayer text cannot be empty');
      return;
    }
    setSavingEdit(true);
    try {
      await axios.put(`${API}/prayers/${editingPrayer.id}`, { prayer_text: trimmed });
      toast.success('Prayer updated');
      setEditDialogOpen(false);
      refreshCurrent();
    } catch {
      toast.error('Failed to update prayer');
    } finally {
      setSavingEdit(false);
    }
  };

  if (loadingGroups) {
    return null;
  }
  if (!groups.length) {
    return null; // Nothing to show — MyGroupsPanel handles the empty state.
  }

  return (
    <Card data-testid="my-group-prayers">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-[#2B6B7F]">
          <Users className="w-5 h-5" />
          Prayers in Your Groups
        </CardTitle>
        <CardDescription>
          Only members of each group can see these prayers. Click a group to open its feed.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-2">
        {groups.map((group) => {
          const isOpen = expandedId === group.id;
          const list = prayersByGroup[group.id] || [];
          return (
            <div key={group.id} className="border border-slate-200 rounded-lg overflow-hidden">
              <button
                type="button"
                onClick={() => toggleGroup(group.id)}
                className="w-full flex items-center justify-between gap-3 p-3 bg-slate-50 hover:bg-slate-100 transition-colors text-left"
                data-testid={`group-prayers-toggle-${group.id}`}
              >
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-slate-800 truncate">{group.name}</p>
                  <p className="text-xs text-slate-500">
                    {(group.members?.length ?? group.member_count ?? 0)} members
                  </p>
                </div>
                {isOpen ? <ChevronUp className="w-4 h-4 text-slate-500" /> : <ChevronDown className="w-4 h-4 text-slate-500" />}
              </button>

              {isOpen && (
                <div className="p-3 space-y-3 bg-white" data-testid={`group-prayers-feed-${group.id}`}>
                  {loadingGroupPrayers === group.id ? (
                    <p className="text-sm text-slate-500 text-center py-2">Loading prayers…</p>
                  ) : list.length === 0 ? (
                    <p className="text-sm text-slate-500 italic text-center py-2">
                      No prayers in this group yet. Post one from the &ldquo;Post a Prayer&rdquo; button and choose &ldquo;My Group Only&rdquo;.
                    </p>
                  ) : (
                    list.map((p) => {
                      const isAuthor = user && p.user_id === user.id;
                      const hasPrayed = user && (p.prayer_user_ids || []).includes(user.id);
                      return (
                        <div
                          key={p.id}
                          className="p-3 rounded-md border border-slate-100 bg-slate-50/40"
                          data-testid={`group-prayer-${p.id}`}
                        >
                          <div className="flex items-start justify-between gap-2 mb-1">
                            <div>
                              <p className="font-medium text-sm text-slate-800">{p.user_name}</p>
                              <p className="text-xs text-slate-500">
                                {new Date(p.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                                {p.prayer_type && p.prayer_type !== 'petition' && ` • ${p.prayer_type}`}
                              </p>
                            </div>
                            {p.answered && (
                              <Badge className="bg-amber-100 text-amber-800 border-amber-200 flex-shrink-0">
                                <Sparkles className="w-3 h-3 mr-1" />
                                Praise Report
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm text-slate-700 whitespace-pre-wrap mb-3">{p.prayer_text}</p>

                          <div className="flex flex-wrap items-center gap-2">
                            <Button
                              size="sm"
                              variant={hasPrayed ? 'default' : 'outline'}
                              disabled={isAuthor || prayingId === p.id}
                              onClick={() => handlePray(p)}
                              className={hasPrayed ? 'bg-[#2B6B7F] hover:bg-[#245968]' : ''}
                              data-testid={`group-prayer-pray-btn-${p.id}`}
                            >
                              <HandHelping className="w-3.5 h-3.5 mr-1" />
                              I prayed for this
                              {isAuthor && typeof p.prayer_count === 'number' && p.prayer_count > 0 && (
                                <span className="ml-1.5 text-xs opacity-80">({p.prayer_count})</span>
                              )}
                            </Button>

                            {isAuthor && (
                              <>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleMarkAnswered(p, !p.answered)}
                                  data-testid={`group-prayer-answered-btn-${p.id}`}
                                >
                                  <Check className="w-3.5 h-3.5 mr-1" />
                                  {p.answered ? 'Unmark' : 'Mark Answered'}
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => openEdit(p)}
                                  data-testid={`group-prayer-edit-btn-${p.id}`}
                                >
                                  <Edit3 className="w-3.5 h-3.5 mr-1" />
                                  Edit
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleDelete(p)}
                                  className="text-red-600 border-red-200 hover:bg-red-50"
                                  data-testid={`group-prayer-delete-btn-${p.id}`}
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </Button>
                              </>
                            )}
                          </div>

                          <GroupPrayerComments prayer={p} compact />
                        </div>
                      );
                    })
                  )}
                </div>
              )}
            </div>
          );
        })}
      </CardContent>

      {/* Edit dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>Edit your prayer</DialogTitle>
            <DialogDescription>Group prayers update immediately — no admin approval needed.</DialogDescription>
          </DialogHeader>
          <Textarea
            rows={5}
            value={editText}
            onChange={(e) => setEditText(e.target.value)}
            data-testid="group-prayer-edit-textarea"
          />
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setEditDialogOpen(false)} disabled={savingEdit}>
              Cancel
            </Button>
            <Button onClick={submitEdit} disabled={savingEdit} className="bg-[#2B6B7F]" data-testid="group-prayer-edit-save-btn">
              {savingEdit ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default MyGroupPrayers;
